import { useCallback, useMemo, useState } from 'react';
import { Group, PersonAdd, Queue } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { ApiService, CustomPrimaryUser } from 'api';
import { BULK_ADD, INVITE_USER, MANAGE_COMPANY_USERS } from 'common/constants/user';
import { CompanyByFirm } from 'common/types/company';
import { manageApiRequest } from 'hooks/apiRequest';
import { BaseItem } from 'pages/UserPermissions/common/types';
import { USER_PERMISSIONS_TEST_IDS } from 'pages/UserPermissions/UserPermissions.testIds';
import useGetCompanyUsers from 'services/hooks/useGetCompanyUsers';
import { useGetFirmUsersSlim } from 'services/hooks/useGetFirmUsers';
import ActionDialogPicker from '../ActionDialogPicker';
import UsersTable from '../UsersTable';
import { UserDirectoryProps } from './UserDirectory.props';
import { StyledGridContainerSubtitle } from './UserDirectory.styles';
import { InviteUserLedgerFormState, Role } from '../InviteUserLedger/InviteUserLedger.props';

const UserDirectory = ({
  selectedFirm,
  selectedCompany,
  handleAddUser,
  companyList,
  fundList,
  setCurrentUser,
  isSuperUser,
  isFirmAdmin,
  is409a = false,
  currentUser,
}: UserDirectoryProps) => {

  const [dialogUserData, setDialogUserData] = useState({
    open: false,
    type: '',
  });

  const handleDialogClose = useCallback(() => {
    setDialogUserData({
      open: false,
      type: '',
    });
  }, []);

  const [userSearchValue, setUserSearchValue] = useState<string>('');
  const { data: firmUsers, isLoading: isFirmLoading } = useGetFirmUsersSlim(selectedFirm?.id, userSearchValue);
  const {
    data: firmUsersForCompanyLedger,
    isLoading: isCompanyLedgerFirmUsersLoading,
  } = useGetFirmUsersSlim(selectedFirm?.id);
  const { data: companyUsers, isLoading: isCompanyDataLoading } = useGetCompanyUsers(
    selectedCompany?.id,
    userSearchValue
  );
  const queryClient = useQueryClient();

  const invalidateCompanyUsersQuery = useCallback(() => {
    if (selectedCompany?.id) {
      queryClient.invalidateQueries({
        queryKey: ['companyUsers', selectedCompany.id],
      });
    }
    handleDialogClose();
  }, [selectedCompany, queryClient, handleDialogClose]);

  const { users, isLoading } = useMemo(() => {
    if (selectedCompany?.id) {
      return { users: companyUsers, isLoading: isCompanyDataLoading };
    }
    return { users: firmUsers, isLoading: isFirmLoading };
  }, [selectedCompany, companyUsers, firmUsers, isCompanyDataLoading, isFirmLoading]);

  const userManagementCompanies = useMemo(() => companyList ?? [], [companyList]);
  const userManagementFunds = useMemo(() => fundList ?? [], [fundList]);
  const canManageCompanyUsers = !isEmpty(userManagementCompanies);

  const handleAddUserFn = useCallback(
    (userData: InviteUserLedgerFormState['values'], role: Role) => {
      handleAddUser(userData, role, invalidateCompanyUsersQuery);
    },
    [handleAddUser, invalidateCompanyUsersQuery]
  );

  const togglePrimaryUser = useCallback(
    async (user: CustomPrimaryUser) => {
      if (!selectedCompany?.id) {
        return;
      }
      await manageApiRequest(
        ApiService.apiDashboardCompanyAddUserCreate({
          ...user,
          is_primary_contact: !user.is_primary_contact,
          company_id: selectedCompany.id,
        })
      );
      invalidateCompanyUsersQuery();
    },
    [invalidateCompanyUsersQuery, selectedCompany]
  );

  return (
    <>
      {(selectedFirm || selectedCompany) && (
        <>
          <Typography variant="h6" data-testid={USER_PERMISSIONS_TEST_IDS.userPermissionsHeading}>User Permissions</Typography>
          <StyledGridContainerSubtitle
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
            <div>
              <Button
                color="primary"
                variant="outlined"
                sx={{ paddingTop: '0.25rem', paddingBottom: '0.25rem', marginRight: '0.5rem' }}
                startIcon={<Queue />}
                onClick={() =>
                  setDialogUserData({
                    type: 'bulkAdd',
                    open: true,
                  })
                }>
                {BULK_ADD}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                sx={{ paddingTop: '0.25rem', paddingBottom: '0.25rem', marginRight: '0.5rem' }}
                startIcon={<PersonAdd />}
                data-testid={USER_PERMISSIONS_TEST_IDS.inviteUserButton}
                onClick={() =>
                  setDialogUserData({
                    type: 'inviteUser',
                    open: true,
                  })
                }>
                {INVITE_USER}
              </Button>
              {canManageCompanyUsers && (
                <Button
                  color="primary"
                  variant="outlined"
                  sx={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                  startIcon={<Group />}
                  disabled={isCompanyLedgerFirmUsersLoading}
                  data-testid={USER_PERMISSIONS_TEST_IDS.manageCompanyUsersButton}
                  onClick={() =>
                    setDialogUserData({
                      type: 'manageCompanyUsers',
                      open: true,
                    })
                  }>
                  {MANAGE_COMPANY_USERS}
                </Button>
              )}
            </div>
          </StyledGridContainerSubtitle>
          {(selectedFirm || selectedCompany) && (
            <ActionDialogPicker
              dialogUserData={dialogUserData}
              handleDialogClose={handleDialogClose}
              firm={{
                id: selectedFirm?.id ?? 0,
                name: selectedFirm?.name ?? '',
              }}
              company={{
                id: selectedCompany?.id as number,
                name: selectedCompany?.name as string,
              }}
              selectedCompany={selectedCompany as BaseItem}
              handleAddUserToFirm={handleAddUserFn}
              companyList={
                (userManagementCompanies.map(({ company_id, company_name }) => ({
                  company_id,
                  company_name,
                })) as CompanyByFirm[]) || []
              }
              fundList={userManagementFunds.map(({ id, name }) => ({ id, name }))}
              firmUsers={firmUsersForCompanyLedger ?? []}
            />
          )}
          <UsersTable
            firm={selectedFirm}
            setCurrentUser={setCurrentUser}
            isSuperUser={isSuperUser}
            isFirmAdmin={isFirmAdmin}
            users={users}
            isLoading={isLoading}
            userSearchValue={userSearchValue}
            setUserSearchValue={setUserSearchValue}
            togglePrimaryUser={togglePrimaryUser}
            is409a={is409a}
            currentUser={currentUser}
          />
        </>
      )}
    </>
  );
};

export default UserDirectory;
